// STRENGTHLEAP — central editable brand data.
// Delivery model: live, private 1:1 virtual sessions (Zoom / Google Meet) + Guided Performance Coaching (async track).
// Replace image URLs with original StrengthLeap photography before launch.

const u = (id, w = 1600) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

export const IMG = {
  hero: u("1710736460914-4a7f22d736c4", 2000),
  runner: u("1609377375724-8fadc82cd50e"),
  handstand: u("1726195220120-8ae07f34b6cb"),
  jump: u("1589104666851-dffe3a15aace"),
  ice: u("1589050054157-fd54651c9483"),
  barbell: u("1620188526357-ff08e03da266"),
  barbellFemale: u("1722925541142-5db2668ca492"),
  coldPlunge:
    "https://images.pexels.com/photos/14815629/pexels-photo-14815629.jpeg?auto=compress&cs=tinysrgb&w=1600",
  gymIndustrial:
    "https://images.pexels.com/photos/9958668/pexels-photo-9958668.jpeg?auto=compress&cs=tinysrgb&w=1600",
  deadlift:
    "https://images.pexels.com/photos/19132573/pexels-photo-19132573.jpeg?auto=compress&cs=tinysrgb&w=1600",
};

export const BRAND = {
  name: "STRENGTHLEAP",
  descriptor: "Human Performance",
  domain: "strengthleap.com",
  url: "https://strengthleap.com",
  email: "coaching@strengthleap.com", // placeholder — replace with live inbox
  whatsapp: "919999999999", // PLACEHOLDER — replace with the real WhatsApp business number (country code + number, digits only)
  positioning: "Private 1:1 performance coaching, delivered live over video — anywhere in the world.",
  tagline: "Recover. Build. Perform.",
  credibility:
    "100% Live 1:1 Video Coaching · Real-Time Form Correction · Head Coach Supervised Protocols",
};

export const NAV_LINKS = [
  { label: "Programs", path: "/programs", testid: "nav-link-programs" },
  { label: "Method", path: "/#method", testid: "nav-link-method" },
  { label: "Performance", path: "/performance", testid: "nav-link-performance" },
  { label: "Corporate", path: "/corporate", testid: "nav-link-corporate" },
  { label: "About", path: "/about", testid: "nav-link-about" },
];

export const METHOD_STEPS = [
  {
    n: "01",
    title: "ASSESS",
    copy: "A live video diagnostic of movement, goals, training history, lifestyle and limitations.",
  },
  {
    n: "02",
    title: "RESTORE",
    copy: "Address pain, mobility restrictions, weak movement patterns and recovery — coached live.",
  },
  {
    n: "03",
    title: "BUILD",
    copy: "Develop strength, conditioning, body composition and resilience, rep by rep, on camera.",
  },
  {
    n: "04",
    title: "PERFORM",
    copy: "Prepare specifically for sport, endurance, HYROX, running or higher physical capability.",
  },
  {
    n: "05",
    title: "EVOLVE",
    copy: "Your coach progresses the program as you adapt. Nothing static. Nothing generic.",
  },
];

export const LIVE_STEPS = [
  {
    n: "01",
    title: "Live Video Diagnostic",
    copy: "A 45-minute live movement screen evaluating joint mobility, posture, movement limitations and strength baselines.",
    detail: "Zoom / Google Meet",
  },
  {
    n: "02",
    title: "Private 1:1 Live Training",
    copy: "Scheduled live video sessions where your coach watches every rep, cues your breathing and mechanics, and adjusts difficulty in real time.",
    detail: "8–12 sessions / month",
  },
  {
    n: "03",
    title: "Lifestyle & Accountability Concierge",
    copy: "Direct WhatsApp access for daily nutrition accountability, ergonomic setup checks and recovery guidance between live sessions.",
    detail: "Between sessions",
  },
];

export const PILLARS = [
  {
    key: "recover",
    title: "RECOVER",
    tagline: "Fix Desk Pain & Restore Joint Freedom",
    forWho:
      "Remote executives, desk-bound professionals with neck/back pain, and individuals resuming movement post-injury.",
    delivery:
      "Live 1:1 postural re-education, guided corrective routines and ergonomic workplace setup.",
    outcome: "Confident, pain-free movement — and a body ready for real training.",
    image: IMG.ice,
    path: "/recovery",
    cta: "Explore RECOVER",
  },
  {
    key: "build",
    title: "BUILD",
    tagline: "Durable Strength & Body Transformation",
    forWho:
      "Professionals wanting lean muscle, functional strength and fat loss — without wasting time in crowded gyms.",
    delivery:
      "Live guided resistance workouts using your home setup or gym gear, paired with realistic nutrition targets.",
    outcome: "Lean muscle, functional strength and visible change — on your schedule.",
    image: IMG.barbellFemale,
    path: "/programs#build",
    cta: "Explore BUILD",
  },
  {
    key: "perform",
    title: "PERFORM",
    tagline: "Live Race & Event Coaching",
    forWho:
      "HYROX competitors, marathon runners and hybrid athletes aiming for target finish times.",
    delivery:
      "Live biomechanical analysis for running, rowing, ski-erg and sled work, plus periodized energy-system pacing.",
    outcome: "A race-ready engine, efficient mechanics and a pacing plan for event day.",
    image: IMG.runner,
    path: "/performance",
    cta: "Explore PERFORM",
  },
  {
    key: "longevity",
    title: "LONGEVITY",
    tagline: "Lifelong Balance & Joint Independence",
    forWho:
      "Adults 40+ and seniors seeking joint preservation, vestibular balance, fall prevention and vitality.",
    delivery:
      "Low-impact, safe, live one-on-one sessions that prioritize joint health and steady stamina.",
    outcome: "Joint independence, balance and stamina that protect how you live.",
    image: IMG.handstand,
    path: "/programs#longevity",
    cta: "Explore LONGEVITY",
  },
];

export const COMPARISON = [
  {
    label: "Delivery",
    app: "Passive PDF & video templates",
    live: "A dedicated human expert on camera with you, every session",
  },
  {
    label: "Technique",
    app: "No real-time safety checks",
    live: "Immediate form correction as you move",
  },
  {
    label: "Adherence",
    app: "High dropout rates, zero human accountability",
    live: "Guaranteed accountability — your coach is waiting for you",
  },
  {
    label: "Injury history",
    app: "Zero injury consideration",
    live: "Joint-aware progressions built around your body and history",
  },
];

export const PERFORMANCE_OFFERINGS = [
  "HYROX preparation",
  "Marathon preparation",
  "Strength & conditioning",
  "Live biomechanical analysis",
  "Race strategy & pacing",
  "Recovery programming",
];

export const CORPORATE_OFFERINGS = [
  "Desk-pain programs",
  "Ergonomic workplace assessments",
  "Posture & mobility sessions",
  "Live group fitness",
  "Employee movement screens",
  "Wellness challenges",
  "1:1 executive coaching",
  "Nutrition education",
];

// EDIT BEFORE LAUNCH — photo & qualifications are still placeholders; name/role confirmed.
export const HEAD_COACH = {
  name: "Doneev Lama",
  role: "Head Coach & Co-Founder",
  photo: null, // PLACEHOLDER — replace with Doneev's professional photograph URL or /images/head-coach.jpg
  bio: "StrengthLeap is built around a Head Coach-led methodology. Doneev develops the protocols, sets the coaching standard, and supervises every live program delivered under the system.",
  qualifications: [
    "Qualification — replace before launch",
    "Certification — replace before launch",
    "Achievement — replace before launch",
  ],
  expertise: [
    "Rehabilitation",
    "Strength",
    "Endurance",
    "Athlete preparation",
    "Executive desk health",
    "HYROX",
    "Movement",
    "Pain management",
  ],
};

// Replace with verified StrengthLeap case studies before launch.
export const CASE_STUDIES = [
  {
    n: "01",
    title: "Back to training after recurring knee pain.",
    track: "Recover → Build",
    status: "Case study in documentation — published with client consent.",
  },
  {
    n: "02",
    title: "First HYROX preparation cycle.",
    track: "Build → Perform",
    status: "Case study in documentation — published with client consent.",
  },
  {
    n: "03",
    title: "Executive desk-health transformation.",
    track: "Restore → Longevity",
    status: "Case study in documentation — published with client consent.",
  },
];

export const PERSONAS = [
  { name: "The Remote Executive", note: "Desk pain, low energy and a calendar that never clears." },
  { name: "The Beginner", note: "A coach beside you from the very first rep — not a template." },
  { name: "The Athlete", note: "Structured live preparation for competitive demands." },
  { name: "The Runner", note: "Durability, mechanics and the strength behind both." },
  { name: "The HYROX Competitor", note: "Race-specific engine, strength and strategy, coached live." },
  { name: "The 40+ Client", note: "Joint independence, balance and long-term vitality." },
  { name: "The Person Returning From Injury", note: "A guided, supervised path back to confident movement." },
];

export const ONLINE_FEATURES = [
  "Live sessions on Zoom / Google Meet",
  "Real-time technique correction",
  "WhatsApp accountability between sessions",
  "Ergonomic setup checks",
  "Nutrition targets & habit support",
  "Head Coach supervised protocols",
];

export const REGIONS = ["India", "United States", "Australia", "UAE", "Singapore", "United Kingdom"];

// Prices are edited here only. Fixed monthly rates.
export const PRICING = {
  INR: { label: "India", symbol: "₹", dedicated: "₹9,999", head: "₹18,999", guided: "₹8,000" },
  USD: { label: "International", symbol: "$", dedicated: "$299", head: "$499", guided: "$119" },
};

// Asynchronous guided track — rendered as a feature card above the live-tier pricing grid.
export const GUIDED_TIER = {
  badge: "Flexible & Autonomous",
  name: "Guided Performance Coaching",
  blurb:
    "Personalized programming with weekly coaching reviews. Train on your own schedule with complete expert oversight.",
  student: { INR: "₹5,999", USD: "$89" },
  features: [
    "100% Customized Training Blueprint — built around your specific goal (Recover, Build, Perform, or Longevity), schedule, and gym/home equipment",
    "Interactive Digital Workout Log — track your exercises, sets, working weights, and perceived exertion (RPE) with zero guesswork",
    "Weekly Coach Audit & Calibration — your coach reviews your completed workout logs, load progression, and fatigue markers every 7 days, adjusting the plan for the week ahead",
    "Asynchronous Video Form Analysis — upload lifting videos anytime to receive direct technique adjustments and movement cueing",
    "Coach Chat Support — direct weekly messaging for lifestyle, nutrition targets, and schedule modifications",
  ],
  cta: "Apply for Guided Coaching",
};

export const TIERS = [
  {
    key: "dedicated",
    name: "Dedicated Coach",
    suffix: "Live 1:1",
    blurb: "A dedicated certified coach, supervised directly by Head Coach protocols.",
    features: [
      "8–12 private live 1:1 video sessions per month (Zoom / Google Meet)",
      "Dedicated certified coach under Head Coach supervision",
      "Customized nutrition targets & habit support",
      "Direct WhatsApp support between live sessions",
    ],
    cta: "Apply for Dedicated Coach",
  },
  {
    key: "head",
    name: "Head Coach Elite",
    suffix: "Live 1:1",
    blurb: "Specialized for Grade 1/2 injury recovery, executive desk health, or elite HYROX / marathon preparation.",
    features: [
      "8–12 private live 1:1 video sessions directly with the Head Coach",
      "Grade 1/2 injury recovery, executive desk health or elite race preparation",
      "Full sports psychology integration",
      "24/7 priority communication",
    ],
    cta: "Request Head Coach Consultation",
    featured: true,
    badge: "Limited to 10 clients",
  },
];

export const ASSESSMENT_OPTIONS = {
  goals: [
    "Pain / Rehab",
    "Strength",
    "Weight Management",
    "HYROX",
    "Running / Marathon",
    "Sports Performance",
    "General Fitness",
    "Senior Fitness",
    "Corporate Wellness",
    "Other",
  ],
  experience: ["Just starting out", "1–2 years", "3–5 years", "5+ years", "Competitive athlete"],
  coaching: ["Dedicated Coach — Live 1:1", "Head Coach Elite — Live 1:1", "Guided Coaching — Async", "Not Sure"],
  ageRanges: ["18–24", "25–34", "35–44", "45–54", "55–64", "65+"],
  referral: ["Google", "Instagram", "YouTube", "Friend / Referral", "Corporate program", "Other"],
};

export const SOCIALS = [
  { label: "Instagram", href: "https://instagram.com/strengthleap" }, // placeholder
  { label: "YouTube", href: "https://youtube.com/@strengthleap" }, // placeholder
  { label: "LinkedIn", href: "https://linkedin.com/company/strengthleap" }, // placeholder
];
